import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDocument,
  deleteDocument,
  updateDocument,
} from "../../../api/filings/documentsAPI";
import { Handler401 } from "../../../api/utils/responseActions";
import DisplayDocument from "../popup/DisplayDocument";
import ConfirmationModal from "../popup/ConfirmationModal";
import { decodeBase64ToBytes } from "../../../utils/files/decoder";
import { detectFileType } from "../../../utils/files/detectFileType";

function ExpertDocumentList({
  documents = [],
  onDocumentDeleted,
  onDocumentUpdated,
  isCpa = false,
  showAcceptReject = false,
  hideReject = false,
  showDelete = false,
  onOpenUploadModal = null,
  customActions = [],
}) {
  const navigate = useNavigate();

  // --- Status Helpers ---
  const getDocumentStatus = (doc) =>
    doc.status || doc.documentStatus || doc.document_status || null;

  const normalizeStatus = (status) => {
    if (!status) return "PENDING";
    const s = status.toUpperCase();
    if (s === "ACC" || s === "ACN" || s === "ACCEPTED") return "ACCEPTED";
    if (s === "REJ" || s === "REJECTED") return "REJECTED";
    if (s === "ESN") return "SIGNATURE_NEEDED";
    if (s === "ESD") return "SIGNED";
    return "PENDING";
  };

  // --- State ---
  const [displayDocument, setDisplayDocument] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(true);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [mimeType, setMimeType] = useState("application/pdf");

  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false);
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [documentToAction, setDocumentToAction] = useState(null);

  // --- Document Data Fetching ---
  const fetchDocument = async (documentId) => {
    try {
      const response = await getDocument("tax/document", documentId);
      if (response.status === 200) {
        const data = await response.json();
        return data.fileEncodedBase64;
      } else if (response.status === 401) {
        Handler401(navigate);
      }
      return null;
    } catch (err) {
      console.error("Error fetching document:", err);
      return null;
    }
  };

  const handleViewDocument = async (document) => {
    setIsLoadingDocument(true);
    setDisplayDocument(true);
    setDocumentName(document.documentName || "Document");
    const fileEncoded = await fetchDocument(document.documentId);
    if (fileEncoded) {
      const byteArray = decodeBase64ToBytes(fileEncoded);
      const { mimeType: detectedMimeType } = detectFileType(byteArray);
      setMimeType(detectedMimeType || "application/pdf");
      setDocumentFile(fileEncoded);
    }
    setIsLoadingDocument(false);
  };

  // --- Document Actions (Accept/Reject/Delete) ---
  const handleDocumentAction = async (action) => {
    if (!documentToAction) return;
    setActionLoadingId(documentToAction.documentId);
    try {
      const data = {
        status: action === "accept" ? "ACC" : "REJ",
        updateType: "status",
      };
      const response = await updateDocument(
        "tax/document",
        documentToAction.documentId,
        data,
        { includeMetrics: true },
      );
      if (response.ok) {
        const resData = await response.json();
        if (onDocumentUpdated) onDocumentUpdated(resData.summary_metrics);
        setShowAcceptConfirmation(false);
        setShowRejectConfirmation(false);
        setDocumentToAction(null);
      }
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;
    setDeletingId(documentToDelete.documentId);
    try {
      const response = await deleteDocument(
        "tax/document",
        documentToDelete.documentId,
      );
      if (response.ok) {
        if (onDocumentDeleted) onDocumentDeleted();
        setShowDeleteConfirmation(false);
        setDocumentToDelete(null);
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (documents.length === 0) return null;

  return (
    <>
      <div className="document-list-container">
        {documents.map((doc) => {
          const rawStatus = getDocumentStatus(doc);
          const docStatus = normalizeStatus(rawStatus);
          const isProcessing = actionLoadingId === doc.documentId;

          return (
            <div key={doc.documentId} className="document-list-item">
              <button
                type="button"
                onClick={() => handleViewDocument(doc)}
                className="document-list-name"
              >
                {doc.documentName}
              </button>

              <div className="document-list-actions">
                {/* 1. Expert 'Move to Sign' Action */}
                {isCpa &&
                  customActions.map(
                    (action) =>
                      action.showIf(doc) && (
                        <button
                          key={action.label}
                          type="button"
                          className="document-list-action-btn secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick(doc);
                          }}
                        >
                          {action.label}
                        </button>
                      ),
                  )}
                {/* 2. Role-Based Status Rendering */}
                {(() => {
                  const isPOA = doc.documentType === "PROOF_OF_ACCEPTANCE"; // To skip setting statuses or actions
                  if (isPOA) {
                    // If you want it completely empty:
                    return null;
                  }
                  // --- CPA VIEW ---
                  if (isCpa) {
                    if (docStatus === "ACCEPTED")
                      return (
                        <span className="document-list-accepted-badge">
                          Accepted
                        </span>
                      );
                    if (docStatus === "REJECTED") {
                      return (
                        <button
                          type="button"
                          className="document-list-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenUploadModal(
                              {
                                name: doc.documentType || "EFILE_FEDERAL", // Ensures .name exists
                                label: doc.documentName || "Document",
                                service: doc.service || "PTF",
                              },
                              doc.documentId,
                            ); // The ID goes as the 2nd argument
                          }}
                        >
                          Update
                        </button>
                      );
                    }
                    if (docStatus === "SIGNATURE_NEEDED")
                      return (
                        <span className="document-list-pending-badge">
                          Require Signature
                        </span>
                      );
                    if (docStatus === "SIGNED")
                      return (
                        <span className="document-list-accepted-badge">
                          Signed
                        </span>
                      );

                    // Default for CPA Pending drafts
                    return (
                      <span className="document-list-pending-badge">
                        Pending Review
                      </span>
                    );
                  }

                  // --- CUSTOMER (CUS) VIEW ---
                  if (docStatus === "ACCEPTED")
                    return (
                      <span className="document-list-accepted-badge">
                        Accepted
                      </span>
                    );
                  if (docStatus === "REJECTED")
                    return (
                      <span
                        className="document-list-pending-badge"
                        style={{ color: "red" }}
                      >
                        Rejected
                      </span>
                    );

                  // Draft Review Phase
                  if (showAcceptReject && docStatus === "PENDING") {
                    return (
                      <>
                        <button
                          type="button"
                          className="document-list-action-btn"
                          onClick={() => {
                            setDocumentToAction(doc);
                            setShowAcceptConfirmation(true);
                          }}
                          disabled={isProcessing}
                        >
                          Accept
                        </button>
                        {!hideReject && (
                          <button
                            type="button"
                            className="document-list-action-btn"
                            onClick={() => {
                              setDocumentToAction(doc);
                              setShowRejectConfirmation(true);
                            }}
                            disabled={isProcessing}
                          >
                            Reject
                          </button>
                        )}
                      </>
                    );
                  }

                  // Signature Phase
                  if (docStatus === "SIGNATURE_NEEDED") {
                    return (
                      <button
                        type="button"
                        className="document-list-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenUploadModal(
                            {
                              name: "EFILE_SIGNATURE_AUTHORIZATION",
                              label: doc.documentName || "Document",
                              service: doc.service || "PTF",
                            },
                            doc.documentId,
                          );
                        }}
                      >
                        Upload Signed
                      </button>
                    );
                  }
                  if (docStatus === "SIGNED")
                    return (
                      <span className="document-list-accepted-badge">
                        Signed
                      </span>
                    );

                  return (
                    <span className="document-list-pending-badge">
                      Under Review
                    </span>
                  );
                })()}
                {/* 3. Delete Action (if enabled) */}
                {showDelete && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDocumentToDelete(doc);
                      setShowDeleteConfirmation(true);
                    }}
                    disabled={deletingId === doc.documentId}
                    className="document-list-delete"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/delete_red.svg`}
                      alt="Delete"
                    />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {displayDocument && (
        <DisplayDocument
          documentName={documentName}
          documentFile={documentFile}
          mimeType={mimeType}
          isLoadingDocument={isLoadingDocument}
          hideShowDocument={() => setDisplayDocument(false)}
        />
      )}

      {/* --- Confirmation Modals --- */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${
          documentToDelete?.documentName || "this document"
        }"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deletingId !== null}
      />

      <ConfirmationModal
        isOpen={showAcceptConfirmation}
        onClose={() => setShowAcceptConfirmation(false)}
        onConfirm={() => handleDocumentAction("accept")}
        title="Accept Document"
        message="Are you sure you want to accept this draft?"
        confirmText="Accept"
        cancelText="Cancel"
        isLoading={actionLoadingId !== null}
      />

      <ConfirmationModal
        isOpen={showRejectConfirmation}
        onClose={() => setShowRejectConfirmation(false)}
        onConfirm={() => handleDocumentAction("reject")}
        title="Reject Document"
        message="Are you sure you want to reject this draft?"
        confirmText="Reject"
        cancelText="Cancel"
        isLoading={actionLoadingId !== null}
      />
    </>
  );
}

export default ExpertDocumentList;
